import { forwardRef, type CSSProperties, type HTMLAttributes, type ReactNode } from "react";
import { color, radius, space } from "@tokens/index";
import { clsx, type ClassValue } from "clsx";
import { twMerge } from "tailwind-merge";
import { Text } from "./Text";
import { coerceBadgeLabel } from "./badgeLabel";

function cn(...inputs: ClassValue[]) {
    return twMerge(clsx(inputs));
}

/**
 * Badge: a flat, non-interactive label.
 *
 * Each variant is a soft tint fill with a matching foreground. `dot` adds a
 * 6px solid status dot. To make a badge clickable, wrap it in a `<button>`.
 */

export type BadgeVariant = "default" | "neutral" | "accent" | "success" | "warning" | "danger";
export type BadgeSize = "sm" | "md";

export interface BadgeProps extends Omit<HTMLAttributes<HTMLSpanElement>, "style"> {
    variant?: BadgeVariant;
    size?: BadgeSize;
    dot?: boolean;
    /**
     * Apply the all-caps contract to string children. Default `true`. Set
     * `false` for content that must keep its case.
     */
    caps?: boolean;
    /**
     * Ellipsize the label on one line inside the badge. The badge then
     * accepts `maxWidth: 100%` from a flex parent and never overflows it.
     */
    truncate?: boolean;
    children: ReactNode;
    className?: string;
    style?: CSSProperties;
}

const sizePadding: Record<BadgeSize, string> = {
    sm: `${space[1]} ${space[2]}`,
    md: `${space[2]} ${space[3]}`,
};

interface VariantPalette {
    bg: string;
    fg: string;
}

function variantPalette(variant: BadgeVariant): VariantPalette {
    switch (variant) {
        case "success":
            return { bg: color.status.successBg, fg: color.status.successFg };
        case "warning":
            return { bg: color.status.warningBg, fg: color.status.warningFg };
        case "danger":
            return { bg: color.status.dangerBg, fg: color.status.dangerFg };
        case "accent":
            return { bg: color.bg.selected, fg: color.accent.primary };
        case "neutral":
            return { bg: color.bg.hover, fg: color.fg.muted };
        case "default":
            return { bg: color.bg.surface2, fg: color.fg.default };
    }
}

export const Badge = forwardRef<HTMLSpanElement, BadgeProps>(function Badge(
    {
        variant = "default",
        size = "sm",
        dot = false,
        caps = true,
        truncate = false,
        className,
        children,
        style: styleOverride,
        ...rest
    },
    ref,
) {
    const palette = variantPalette(variant);

    const computed: CSSProperties = {
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        gap: space[1],
        padding: sizePadding[size],
        lineHeight: 1,
        borderRadius: radius.pill,
        background: palette.bg,
        color: palette.fg,
        whiteSpace: "nowrap",
        ...(truncate ? { maxWidth: "100%", minWidth: 0, overflow: "hidden" as const } : {}),
    };

    const style: CSSProperties =
        styleOverride !== undefined ? { ...computed, ...styleOverride } : computed;

    const dotStyle: CSSProperties = {
        display: "inline-block",
        width: "6px",
        height: "6px",
        borderRadius: radius.full,
        background: palette.fg,
        flexShrink: 0,
    };

    const role = caps ? "caps" : "bodySm";
    const label = coerceBadgeLabel(children);
    let renderedContent: ReactNode = children;
    if (label !== null) {
        renderedContent = (
            <Text
                role={role}
                size={size === "md" ? "sm" : "xs"}
                truncate={truncate}
                style={{ lineHeight: 1, color: "inherit" }}
            >
                {label}
            </Text>
        );
    }

    return (
        <span
            ref={ref}
            data-variant={variant}
            data-size={size}
            data-caps={caps || undefined}
            style={style}
            className={cn(className)}
            {...rest}
        >
            {dot && <span aria-hidden="true" style={dotStyle} />}
            {renderedContent}
        </span>
    );
});
